/**
 * Skill Center API client (SKC-T1.3 / P-COND-B13A4C16E0A2).
 *
 * Typed wrappers for every route in
 * `planning/skill-center/implementation-plan.md` > "## Interface Contracts" >
 * "API（前缀 /api/v1）". The backend routes land in later tiers (SKC-T2.1,
 * T3.2, T4.1, T5.1, T5.2); until then nothing in the UI calls these functions.
 *
 * Contract status: the URL / method / enum spellings below are FIXED by the
 * plan. Response-object field lists beyond what the plan spells out
 * (e.g. `EffectiveSkill`, `Skill`) are PROVISIONAL — the backend ticket that
 * implements each route owns the final shape and must update this file.
 *
 * Transport: reuses the shared axios instance from `services/api` (same as
 * `services/orgs.ts`) so auth-header injection and 401 → /login stay uniform.
 * Errors bubble up as raw `AxiosError`; callers branch on
 * `error.response?.status` (409 = revision / version conflict). The single
 * exception is `fetchSkillCapabilities`, which never throws (see below).
 */

import type { AxiosInstance } from 'axios';
import api from './api';

// ---------------------------------------------------------------------------
// Enums (spelled exactly as the plan's Interface Contracts)
// ---------------------------------------------------------------------------

/** `skills.scope`. */
export type SkillScope = 'org' | 'department' | 'project' | 'personal';
/** `skill_assignments.target_kind`. */
export type SkillAssignmentTargetKind = 'platform' | 'db';
/** `skill_assignments.mode`. */
export type SkillAssignmentMode = 'mandatory' | 'default' | 'available';
/** `skill_assignments.state` (`mandatory` implies `active`). */
export type SkillAssignmentState = 'active' | 'inactive';
/** `skill_publish_requests.status`. */
export type SkillPublishRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';
/** `skill_drafts.kind`. */
export type SkillDraftKind = 'new' | 'revision';
/** Per-project delivery outcome carried by every sync-triggering write. */
export type SkillSyncStatus =
  | 'delivered'
  | 'failed'
  | 'skipped_attach_only'
  | 'windows_append_only';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One entry of the `sync` list returned by sync-triggering writes. */
export interface SkillSyncResult {
  project_id: string;
  status: SkillSyncStatus;
  error: string | null;
}

/** Wrapper for write responses that also report per-project delivery. */
export type WithSkillSync<T> = T & { sync: SkillSyncResult[] };

/** `GET /users/me/skills/capabilities`. */
export interface SkillCapabilities {
  skill_center_enabled: boolean;
  personal_skills_enabled: boolean;
  skill_publish_enabled: boolean;
}

/** Returned when the capabilities route is missing (old backend) or errors. */
export const SKILL_CAPABILITIES_DISABLED: Readonly<SkillCapabilities> = Object.freeze({
  skill_center_enabled: false,
  personal_skills_enabled: false,
  skill_publish_enabled: false,
});

/** Why an effective skill is unavailable (`skill_resolver.py::UNAVAILABLE_REASONS`). */
export type SkillUnavailableReason =
  | 'not_installed'
  | 'name_conflict'
  | 'mode_mismatch'
  | 'permission_denied'
  | 'archived'
  | 'not_delivered';

/** How a skill reaches the project (`skill_resolver.py::DELIVERY_MODES`). */
export type SkillDeliveryMode = 'file' | 'attach_only';

/**
 * Source of an effective skill: the platform catalog, a conductor repo-native
 * skill (`skill_resolver.py::SOURCE_CONDUCTOR`, conductor project only; no
 * `skill_id`), or a DB skill scope.
 */
export type EffectiveSkillSource = 'platform' | 'conductor' | SkillScope;

/**
 * One row of `GET /projects/{project_id}/skills/effective` — field names are
 * `skill_resolver.py::EffectiveSkill.to_dict` (SKC-T2.1, authoritative).
 * `unavailable_reason` is null iff `available`. `mode` is the ASSIGNMENT mode
 * (null = no assignment row: personal skill, or a platform default/never
 * assigned). `delivered_version` / `failed_version` come from the
 * `skill_deliveries` ledger (file-delivered DB skills only).
 */
export interface EffectiveSkill {
  slug: string;
  name: string;
  description: string;
  description_zh?: string | null;
  source: EffectiveSkillSource;
  skill_id: string | null;
  version: number | null;
  mode: SkillAssignmentMode | null;
  mandatory: boolean;
  available: boolean;
  unavailable_reason: SkillUnavailableReason | null;
  delivery_mode: SkillDeliveryMode;
  gate_eligible: boolean;
  delivered_version?: number | null;
  failed_version?: number | null;
  category?: string | null;
  sort_order?: number | null;
  // SKC-T7 ST2 (`api/skills.py::EffectiveSkillOut`) — optional so older
  // fixtures / shadow responses still type-check.
  /** Modes the bound version declares; null = author did not restrict. */
  modes?: string[] | null;
  delivery_status?: SkillDeliveryStatus;
  last_error?: string | null;
  guide_version?: number | null;
  pending_version?: number | null;
  latest_modes?: string[] | null;
  running_version_unknown?: boolean;
  has_guide?: boolean;
  title_zh?: string | null;
  summary_zh?: string | null;
  guide_brief?: SkillGuideBrief | null;
  // SKC-T9 install model (`api/skills.py::EffectiveSkillOut`, SKC-T9.4) —
  // optional until the backend ships them; nothing renders differently without.
  /** Platform / unassigned org skill: the caller's personal install record. */
  user_installed?: boolean;
  /** Why the skill is in the caller's picker ('none' = not installed). */
  install_source?: SkillInstallSource;
  /** `available && install_source != 'none'` — the only picker / favourite gate. */
  in_picker?: boolean;
  /** Display name of the creator; null for platform skills. */
  creator_name?: string | null;
}

/** `EffectiveSkillOut.install_source` (SKC-T9 install model). */
/** SKC-T10 (V4): `org_default` = an org skill with 组织内默认安装 on that the
 *  caller has not removed (priority mandatory > org_default > user). */
export type SkillInstallSource = 'mandatory' | 'project' | 'org_default' | 'user' | 'owner' | 'none';

/**
 * SKC-T9.7 (R43 / R45): the ONE picker gate for the composer — the "+" panel,
 * favourite chips and favourites tab all read it. `in_picker` is authoritative;
 * a response from a backend that predates SKC-T9.4 (field absent) falls back
 * to `available`.
 */
export function isInPicker(s: EffectiveSkill): boolean {
  return typeof s.in_picker === 'boolean' ? s.in_picker : s.available;
}

/** Read-time delivery status (Contract Pack 页面状态推导). */
export type SkillDeliveryStatus = 'delivered' | 'failed' | 'pending' | 'stale' | 'attach' | 'none';

/** Card 「展开说明」 three items. */
export interface SkillGuideBrief {
  when?: string | null;
  inputs?: string | null;
  output?: string | null;
}

/** `api/skills.py::SkillModeContext`. `effective_mode=null` = project type unset (no filter). */
export interface EffectiveSkillsContext {
  project_modes: string[] | null;
  effective_mode: string | null;
  mode_source: 'request' | 'project' | 'conversation' | 'none';
}

/** `mode` query of the effective list (`project` = derive from project type). */
export type EffectiveSkillsMode = 'dev' | 'collab' | 'project';
export type ConversationSkillMode = 'dev' | 'collab';

export interface EffectiveSkillsResponse {
  project_id: string;
  mode: string | null;
  /** SKC-T7 ST2; optional for old fixtures. */
  context?: EffectiveSkillsContext | null;
  /** False = shadow mode (`skills_unified_resolver_enabled` off). */
  enforced: boolean;
  skills: EffectiveSkill[];
}

/** `GET /projects/{id}/skills/guide` (`api/skills.py::SkillGuideOut`). */
export interface SkillGuide {
  key: string;
  title: string;
  summary: string | null;
  when: string | null;
  inputs: string | null;
  output: string | null;
  how: string | null;
  example: string | null;
  notes: string | null;
  source: string;
  version: number | null;
  pending_version: number | null;
  has_guide: boolean;
  /** SKC-T9.4: creator display name; null / absent for platform skills. */
  creator_name?: string | null;
}

/** Guide lookup key: platform skills by slug, DB skills by id (never by name). */
export type SkillGuideKey = { slug: string } | { skill_id: string };

/** A DB skill row (personal / project / department / org). PROVISIONAL. */
export interface Skill {
  id: string;
  scope: SkillScope;
  slug: string;
  title?: string;
  description: string;
  org_id: string | null;
  department_id: string | null;
  project_id: string | null;
  owner_user_id: string | null;
  current_version: number | null;
  archived_at?: string | null;
  /** `user_skills.py::_skill_out` sends a boolean instead of `archived_at`. */
  archived?: boolean;
  created_at: string;
  updated_at: string;
}

/** Immutable `skill_versions` row. */
export interface SkillVersion {
  skill_id: string;
  version: number;
  body?: string;
  sha256: string;
  size_bytes: number;
  created_by: string;
  created_at: string;
}

/** `skill_assignments` item for `PUT /projects/{id}/skills/assignments`. */
export interface SkillAssignmentInput {
  target_kind: SkillAssignmentTargetKind;
  platform_slug?: string | null;
  skill_id?: string | null;
  mode: SkillAssignmentMode;
  state: SkillAssignmentState;
}

export interface SkillAssignment extends SkillAssignmentInput {
  id: string;
  project_id: string;
}

/**
 * `skill_drafts` row as returned by `GET /users/me/skills/drafts[/{id}]`
 * (SKC-T4.1). `updated_at` / `saved_at` are `+08:00` ISO strings
 * (`to_cst_iso`). Fields beyond the T4.1 list response are optional.
 */
export interface SkillDraft {
  id: string;
  user_id?: string;
  conversation_id: string;
  kind: SkillDraftKind;
  skill_id: string | null;
  base_version: number | null;
  title: string | null;
  body: string | null;
  revision: number;
  confirmed_at?: string | null;
  saved_at: string | null;
  created_at?: string;
  updated_at: string;
  /** Host project of the refine conversation. */
  project_id?: string | null;
  /** UTF-8 size of `body` (the list response omits `body` itself). */
  size_bytes?: number;
  /**
   * Detail only: current version of the target skill — `skill_id` (revision
   * drafts) or `saved_skill_id` (a new draft already saved once). Save-again
   * of a new draft sends it as `base_version`.
   */
  current_version?: number | null;
  saved_skill_id?: string | null;
}

/** `POST /users/me/skills/drafts` response. */
export interface CreateSkillDraftResponse {
  conversation_id: string;
  draft_id: string;
}

/** `POST /users/me/skills/drafts` body: host project (+ `skill_id` to revise). */
export interface CreateSkillDraftRequest {
  from_project?: string | null;
  project_id?: string | null;
  skill_id?: string | null;
  /** Optional skill name; also titles the refine conversation. */
  title?: string | null;
  /** Workspace mode of the refine conversation (`dev` / `collab`). */
  mode?: string | null;
}

/** `PUT /users/me/skills/drafts/{id}` body (409 when `revision` is stale). */
export interface UpdateSkillDraftRequest {
  title?: string | null;
  body?: string | null;
  revision: number;
}

/** `POST /users/me/skills/drafts/{id}/save` body. */
export interface SaveSkillDraftRequest {
  confirmed: true;
  base_version: number | null;
  /** Draft revision the user checked; a newer agent write → 409. */
  revision?: number;
  /** New skill: the invocation name (REQUIRED by the backend for kind=new). */
  slug?: string;
  title?: string;
  /** Skill description (frontmatter `description` of the draft). */
  description?: string;
}

/** `POST /users/me/skills/drafts/{id}/save` response. */
export interface SaveSkillDraftResponse {
  skill: Skill;
  version: SkillVersion;
  draft: SkillDraft;
}

/**
 * Error body of the personal-skill routes: `{detail: {code, message, ...}}`
 * (`user_skills.py::_err`) or a plain `{detail: "..."}`.
 */
export function errorDetail(err: unknown): { code: string | null; message: string | null } {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof d === 'string') return { code: null, message: d || null };
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>;
    return {
      code: typeof o.code === 'string' ? o.code : null,
      message: typeof o.message === 'string' ? o.message : null,
    };
  }
  return { code: null, message: null };
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * User-facing text for a failed Skill Center action (SKC-T9.3, R42).
 * - 5xx, no response, or a `detail` that is not a JSON object (plain string,
 *   HTML error page, …) → `skillCenter.errors.generic`; raw server text is
 *   never shown.
 * - `{detail: {code}}` → `skillCenter.errors.<code>`; an unknown code falls
 *   back to `detail.message`, then to the generic text.
 */
export function skillActionError(t: Translate, err: unknown): string {
  const generic = t('skillCenter.errors.generic');
  const status = errorStatus(err);
  if (status == null || status >= 500) return generic;
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  // SKC-T9.7: the install route reports an unmanaged SKILL.md as a plain
  // string detail; give it its own text when the key exists.
  if (typeof d === 'string' && d.includes('unmanaged_file_exists')) {
    const key = 'skillCenter.errors.unmanaged_file_exists';
    const text = t(key);
    return text !== key ? text : generic;
  }
  if (!d || typeof d !== 'object' || Array.isArray(d)) return generic;
  const { code, message } = errorDetail(err);
  if (code) {
    const key = `skillCenter.errors.${code}`;
    const text = t(key);
    if (text !== key) return text;
  }
  return message || generic;
}

/** 409 body of draft-save / new-version: the current version, draft kept. */
export interface SkillVersionConflictDetail {
  current_version: number;
  current_body: string;
}

/**
 * User-authored skill body limit, bytes (UTF-8). Mirrors the backend
 * `business_constants.py::PERSONAL_SKILL_MAX_BYTES` (the backend enforces it;
 * the frontend only shows the size and blocks obviously-over submits).
 */
export const PERSONAL_SKILL_MAX_BYTES = 16384;

/** UTF-8 byte length (what PERSONAL_SKILL_MAX_BYTES counts). */
export function utf8Bytes(text: string | null | undefined): number {
  if (!text) return 0;
  // Manual count (jsdom has no TextEncoder): surrogate pairs = 4 bytes.
  let n = 0;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < text.length) {
      n += 4;
      i += 1;
    } else n += 3;
  }
  return n;
}

/** HTTP status of an axios-style error, or null. */
export function errorStatus(err: unknown): number | null {
  const st = (err as { response?: { status?: unknown } })?.response?.status;
  return typeof st === 'number' ? st : null;
}

/**
 * Read the current version out of a 409 version-conflict response. Accepts
 * both FastAPI shapes (`{detail: {...}}` and a top-level body) and both
 * `current_version`/`current_body` and `version`/`body` spellings; returns
 * null when the 409 carries no body (e.g. a draft `revision` mismatch).
 */
export function parseVersionConflict(err: unknown): SkillVersionConflictDetail | null {
  if (errorStatus(err) !== 409) return null;
  const data = (err as { response?: { data?: unknown } }).response?.data as
    | Record<string, unknown>
    | undefined;
  const raw = (data && typeof data.detail === 'object' && data.detail !== null
    ? (data.detail as Record<string, unknown>)
    : data) || {};
  const nested = (raw.current && typeof raw.current === 'object'
    ? (raw.current as Record<string, unknown>)
    : null);
  const version = raw.current_version ?? nested?.version ?? raw.version;
  const body = raw.current_body ?? nested?.body ?? raw.body;
  if (typeof version !== 'number' || typeof body !== 'string') return null;
  return { current_version: version, current_body: body };
}

/** Lists may arrive bare or wrapped (`{drafts: [...]}` / `{items: [...]}`). */
function asList<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  const o = data as Record<string, unknown> | null;
  if (o && Array.isArray(o[key])) return o[key] as T[];
  if (o && Array.isArray(o.items)) return o.items as T[];
  return [];
}

export interface CreateSkillRequest {
  slug: string;
  description?: string;
  body: string;
}

export interface UpdateSkillRequest {
  description?: string;
  archived?: boolean;
}

/** New version (personal / org): stale `base_version` → 409. */
export interface CreateSkillVersionRequest {
  body: string;
  base_version: number;
}

/** Publish / transfer target (SKC-T9.5 added `project`; SKC-T10 V5 dropped
 *  `department` — the write APIs answer 422 for it). */
export type SkillTransferScope = 'org' | 'project';

export interface PublishSkillRequest {
  target_scope: SkillTransferScope;
  department_id?: string | null;
  /** Required with `target_scope: 'project'` (422 `project_required`). */
  project_id?: string | null;
  /** Org to publish into (`skill_publish.py::PublishCreate.org_id`); the
   *  backend falls back to the caller's single org when omitted. */
  org_id?: string | null;
}

/**
 * One publish request as `skill_publish.py::_request_out` returns it (SKC-T5.2).
 * `body` (the locked version) only comes from the detail route
 * (`getSkillPublishRequest`); `can_decide` / `can_withdraw` are the server's
 * per-viewer verdicts (self-approval excluded).
 */
export interface SkillPublishRequest {
  id: string;
  org_id: string;
  /** `'department'` only on legacy rows (read / display; SKC-T10 V5). */
  target_scope?: SkillTransferScope | 'department';
  department_id: string | null;
  department_name?: string | null;
  /** SKC-T9.5: project target (`target_scope: 'project'`). */
  project_id?: string | null;
  project_name?: string | null;
  source_skill_id: string;
  /** Legacy provisional spelling of `source_skill_id` (T1.3 stub). */
  skill_id?: string;
  skill_slug?: string | null;
  skill_title?: string | null;
  /** The source skill's CURRENT version (may be newer than `version`). */
  source_current_version?: number | null;
  skill_version_id: string;
  /** Version NUMBER of `skill_version_id` (locked at submit). */
  version?: number | null;
  size_bytes?: number | null;
  target_skill_id: string | null;
  status: SkillPublishRequestStatus;
  requested_by: string;
  requester_name?: string | null;
  decided_by: string | null;
  decider_name?: string | null;
  decision_note?: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at?: string;
  can_decide?: boolean;
  can_withdraw?: boolean;
  /** Detail route only. */
  body?: string | null;
  /** Approve response only (SKC-T9.5): the personal skill was moved in place. */
  transferred?: boolean;
}

/** Source skill id of a publish request (column name + T1.3 stub spelling). */
export function publishRequestSkillId(r: SkillPublishRequest): string {
  return r.source_skill_id || r.skill_id || '';
}

/** Org / department skill row (`org_skills.py::org_skill_out`, SKC-T5.2). */
export interface OrgSkill extends Skill {
  department_name?: string | null;
  /** Projects holding a `mandatory` assignment (archive is 409 while > 0). */
  mandatory_project_count?: number | null;
  mandatory?: boolean;
  assignments?: { project_id: string; mode: SkillAssignmentMode; state: SkillAssignmentState }[];
  /** Current version body (library rows carry it; prefills the editor). */
  body?: string | null;
  /** SKC-T10 (V4): 组织内默认安装 (`org_skills.py::org_skill_out`). */
  default_install: boolean;
}

/** Body of `POST /orgs/{org_id}/skills` (`org_skills.py::OrgSkillCreate`). */
export interface CreateOrgSkillRequest {
  slug: string;
  title?: string;
  description?: string;
  body: string;
  /** SKC-T10 (V5): org only (`OrgSkillCreate.target_scope` = `^org$`). */
  target_scope: 'org';
}

export interface OrgDepartment {
  id: string;
  name: string;
  paused: boolean;
}

/** `PATCH /orgs/{org_id}/departments/{department_id}` (`orgs.py::OrgDepartmentRenameResponse`). */
export interface RenameDepartmentResult extends OrgDepartment {
  old_name: string;
  changed: boolean;
  cascade?: Record<string, number>;
  departments?: string[];
}

/** One department of `GET /orgs/{org_id}/departments` with its id. */
export interface OrgDepartmentRecord {
  /** `department_states[].id` (SKC-T5.1); null for a name with no live
   *  `org_departments` row — rename / department publish need the id. */
  id: string | null;
  name: string;
  paused: boolean;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

const V1 = '/api/v1';

function client(): AxiosInstance {
  return (api as unknown as { client: AxiosInstance }).client;
}

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Effective list + capabilities
// ---------------------------------------------------------------------------

export async function fetchEffectiveSkills(
  projectId: string,
  mode?: EffectiveSkillsMode | string | null,
  conversationMode?: ConversationSkillMode | null,
): Promise<EffectiveSkillsResponse> {
  const params: Record<string, string> = {};
  if (mode) params.mode = mode;
  // Only meaningful with mode=project (backend ignores it otherwise).
  if (mode === 'project' && conversationMode) params.conversation_mode = conversationMode;
  const r = await client().get<EffectiveSkillsResponse>(
    `${V1}/projects/${enc(projectId)}/skills/effective`,
    { params: Object.keys(params).length ? params : undefined },
  );
  return r.data;
}

/** One skill's structured 说明 at the list's bound version (SKC-T7 ST2). */
export async function getSkillGuide(projectId: string, key: SkillGuideKey): Promise<SkillGuide> {
  const params = 'slug' in key ? { slug: key.slug } : { skill_id: key.skill_id };
  const r = await client().get<SkillGuide>(`${V1}/projects/${enc(projectId)}/skills/guide`, {
    params,
  });
  return r.data;
}

/** One capability request; THROWS on any transport / HTTP error. */
async function requestSkillCapabilities(): Promise<SkillCapabilities> {
  const r = await client().get<Partial<SkillCapabilities>>(
    `${V1}/users/me/skills/capabilities`,
  );
  const d = r.data || {};
  return {
    skill_center_enabled: d.skill_center_enabled === true,
    personal_skills_enabled: d.personal_skills_enabled === true,
    skill_publish_enabled: d.skill_publish_enabled === true,
  };
}

/**
 * Capability flags. By default NEVER throws: an old backend (404) or any
 * error resolves to all-false, so Skill Center entries stay hidden (the
 * frontend has no feature-flag mechanism of its own).
 *
 * `{ strict: true }` (SKC-T4.3, Tier 3 review) rethrows instead, so a caching
 * caller (`useEffectiveSkills.ts::loadSkillCapabilities`) can tell a failed
 * fetch from a genuine all-false answer and avoid memoizing the failure.
 */
export async function fetchSkillCapabilities(
  opts?: { strict?: boolean },
): Promise<SkillCapabilities> {
  if (opts?.strict) return requestSkillCapabilities();
  try {
    return await requestSkillCapabilities();
  } catch {
    return { ...SKILL_CAPABILITIES_DISABLED };
  }
}

// ---------------------------------------------------------------------------
// Project assignments + sync
// ---------------------------------------------------------------------------

/**
 * Partial UPSERT of project assignments (SKC-T3.2 `projects.py::put_skill_assignments`,
 * org admin or platform admin). Rows not sent are untouched. The response
 * carries per-project delivery results; only a DB failure is 5xx.
 */
export async function putSkillAssignments(
  projectId: string,
  assignments: SkillAssignmentInput[],
): Promise<WithSkillSync<{ project_id: string; assignments: SkillAssignment[] }>> {
  const r = await client().put<WithSkillSync<{ project_id: string; assignments: SkillAssignment[] }>>(
    `${V1}/projects/${enc(projectId)}/skills/assignments`,
    { assignments },
  );
  return r.data;
}

/** Re-run delivery for undelivered skills only (idempotent, no new version). */
export async function syncProjectSkills(
  projectId: string,
): Promise<{ sync: SkillSyncResult[] }> {
  const r = await client().post<{ sync: SkillSyncResult[] }>(
    `${V1}/projects/${enc(projectId)}/skills/sync`,
  );
  return r.data;
}

// ---------------------------------------------------------------------------
// Personal skills
// ---------------------------------------------------------------------------

export async function listMySkills(): Promise<Skill[]> {
  const r = await client().get<unknown>(`${V1}/users/me/skills`);
  return asList<Skill>(r.data, 'skills');
}

export async function createMySkill(body: CreateSkillRequest): Promise<Skill> {
  const r = await client().post<Skill>(`${V1}/users/me/skills`, body);
  return r.data;
}

export async function updateMySkill(skillId: string, body: UpdateSkillRequest): Promise<Skill> {
  const r = await client().patch<Skill>(`${V1}/users/me/skills/${enc(skillId)}`, body);
  return r.data;
}

export async function deleteMySkill(skillId: string): Promise<void> {
  await client().delete(`${V1}/users/me/skills/${enc(skillId)}`);
}

export async function listMySkillVersions(skillId: string): Promise<SkillVersion[]> {
  const r = await client().get<unknown>(
    `${V1}/users/me/skills/${enc(skillId)}/versions`,
  );
  return asList<SkillVersion>(r.data, 'versions');
}

export async function getMySkillVersion(skillId: string, version: number): Promise<SkillVersion> {
  const r = await client().get<SkillVersion>(
    `${V1}/users/me/skills/${enc(skillId)}/versions/${version}`,
  );
  return r.data;
}

export async function createMySkillVersion(
  skillId: string,
  body: CreateSkillVersionRequest,
): Promise<SkillVersion> {
  const r = await client().post<SkillVersion>(
    `${V1}/users/me/skills/${enc(skillId)}/versions`,
    body,
  );
  return r.data;
}

/** Submit for publishing; the backend locks the CURRENT version at submit time. */
export async function publishMySkill(
  skillId: string,
  body: PublishSkillRequest,
): Promise<SkillPublishRequest> {
  const r = await client().post<SkillPublishRequest>(
    `${V1}/users/me/skills/${enc(skillId)}/publish`,
    body,
  );
  return r.data;
}

/** @deprecated SKC-T9.5 made `copy-to-org` a transfer alias; use
 *  {@link transferSkill}. No frontend caller remains (kept one release). */
export async function copyMySkillToOrg(
  skillId: string,
  body: PublishSkillRequest & {
    /** SKC-T7: the version the confirm dialog showed; a stale one → 409 `version_changed`. */
    version?: number | null;
  },
): Promise<WithSkillSync<OrgSkill>> {
  const r = await client().post<WithSkillSync<OrgSkill>>(
    `${V1}/users/me/skills/${enc(skillId)}/copy-to-org`,
    body,
  );
  return r.data;
}

// ---------------------------------------------------------------------------
// Personal installs + transfer (SKC-T9 install model; backend SKC-T9.4 / T9.5)
// ---------------------------------------------------------------------------

/** `GET /users/me/skill-installs` — keys are platform slugs / DB skill ids. */
/** Install targets grouped by kind: platform slugs and DB skill ids. */
export interface SkillInstallTargets {
  platform: string[];
  db: string[];
}

/** `GET|PUT /users/me/skill-installs` (SKC-T9.4). A target in neither group
 *  has no personal record (mandatory / project skills need none). */
export interface SkillInstallsResponse {
  installed: SkillInstallTargets;
  removed: SkillInstallTargets;
  /** True once the caller's defaults were seeded (`seed_user_installs`). */
  seeded: boolean;
}

/** `PUT /users/me/skill-installs` body: exactly one of slug / id (else 422
 *  `bad_target`). `project_id` (optional) judges "mandatory" in that project
 *  only; omitted → any accessible project. */
export type SetSkillInstallRequest =
  | { platform_slug: string; skill_id?: never; installed: boolean; project_id?: string }
  | { skill_id: string; platform_slug?: never; installed: boolean; project_id?: string };

export async function getSkillInstalls(): Promise<SkillInstallsResponse> {
  const r = await client().get<SkillInstallsResponse>(`${V1}/users/me/skill-installs`);
  return r.data;
}

/** Install / uninstall for the caller; 409 `mandatory`, 404 `not_found` when not usable. */
export async function setSkillInstall(body: SetSkillInstallRequest): Promise<SkillInstallsResponse> {
  const r = await client().put<SkillInstallsResponse>(`${V1}/users/me/skill-installs`, body);
  return r.data;
}

export interface TransferSkillRequest {
  target_scope: SkillTransferScope;
  org_id?: string | null;
  department_id?: string | null;
  project_id?: string | null;
  /** Version the confirm dialog showed; a stale one → 409 `version_changed`. */
  version: number;
}

/** Admin direct transfer of a personal skill (replaces `copy-to-org`). */
export async function transferSkill(
  skillId: string,
  body: TransferSkillRequest,
): Promise<WithSkillSync<Skill>> {
  const r = await client().post<WithSkillSync<Skill>>(
    `${V1}/users/me/skills/${enc(skillId)}/transfer`,
    body,
  );
  return r.data;
}

// ---------------------------------------------------------------------------
// Drafts (skill_refine conversations)
// ---------------------------------------------------------------------------

/**
 * Creates a skill_refine conversation + empty draft. Pass `skill_id` to
 * revise an existing skill (`kind=revision`); `project_id` / `from_project`
 * pick the host project. Null / empty fields are omitted from the body.
 */
export async function createSkillDraft(
  req: CreateSkillDraftRequest = {},
): Promise<CreateSkillDraftResponse> {
  const body: Record<string, string> = {};
  (['from_project', 'project_id', 'skill_id', 'title', 'mode'] as const).forEach((k) => {
    const v = req[k];
    if (v) body[k] = v;
  });
  const r = await client().post<CreateSkillDraftResponse>(`${V1}/users/me/skills/drafts`, body);
  return r.data;
}

/**
 * Filters for `GET /users/me/skills/drafts` (SKC-FLOW-v2). Both optional and
 * always scoped to the current user; no params = unsaved drafts of all
 * conversations (unchanged behaviour). `include_saved` also returns drafts
 * already saved as a skill (the refine card needs them after a refresh).
 */
export interface ListSkillDraftsParams {
  conversation_id?: string;
  include_saved?: boolean;
}

export async function listSkillDrafts(params: ListSkillDraftsParams = {}): Promise<SkillDraft[]> {
  const query: Record<string, string> = {};
  if (params.conversation_id) query.conversation_id = params.conversation_id;
  if (params.include_saved) query.include_saved = 'true';
  const r = Object.keys(query).length
    ? await client().get<unknown>(`${V1}/users/me/skills/drafts`, { params: query })
    : await client().get<unknown>(`${V1}/users/me/skills/drafts`);
  return asList<SkillDraft>(r.data, 'drafts');
}

export async function getSkillDraft(draftId: string): Promise<SkillDraft> {
  const r = await client().get<SkillDraft>(`${V1}/users/me/skills/drafts/${enc(draftId)}`);
  return r.data;
}

export async function updateSkillDraft(
  draftId: string,
  body: UpdateSkillDraftRequest,
): Promise<SkillDraft> {
  const r = await client().put<SkillDraft>(
    `${V1}/users/me/skills/drafts/${enc(draftId)}`,
    body,
  );
  return r.data;
}

export async function saveSkillDraft(
  draftId: string,
  body: SaveSkillDraftRequest,
): Promise<SaveSkillDraftResponse> {
  const r = await client().post<SaveSkillDraftResponse>(
    `${V1}/users/me/skills/drafts/${enc(draftId)}/save`,
    body,
  );
  return r.data;
}

/** Abandon a draft; the refine conversation is kept. */
export async function deleteSkillDraft(draftId: string): Promise<void> {
  await client().delete(`${V1}/users/me/skills/drafts/${enc(draftId)}`);
}

// ---------------------------------------------------------------------------
// Org skills
// ---------------------------------------------------------------------------

export async function listOrgSkills(orgId: string): Promise<OrgSkill[]> {
  const r = await client().get<unknown>(`${V1}/orgs/${enc(orgId)}/skills`);
  return asList<OrgSkill>(r.data, 'skills');
}

/** Admin direct create (audited, no approval). 409 = name taken. */
export async function createOrgSkill(
  orgId: string,
  body: CreateOrgSkillRequest,
): Promise<WithSkillSync<OrgSkill>> {
  const r = await client().post<WithSkillSync<OrgSkill>>(`${V1}/orgs/${enc(orgId)}/skills`, body);
  return r.data;
}

export async function createOrgSkillVersion(
  orgId: string,
  skillId: string,
  body: CreateSkillVersionRequest,
): Promise<WithSkillSync<SkillVersion>> {
  const r = await client().post<WithSkillSync<SkillVersion>>(
    `${V1}/orgs/${enc(orgId)}/skills/${enc(skillId)}/versions`,
    body,
  );
  return r.data;
}

/** Body of `PATCH /orgs/{org_id}/skills/{skill_id}` (`org_skills.py::OrgSkillPatch`). */
export interface OrgSkillPatchRequest {
  archived?: boolean;
  title?: string;
  description?: string;
  /** SKC-T10 (V4): 组织内默认安装 — org admin only. */
  default_install?: boolean;
}

/** Admin patch of an org skill (archive / title / description / default install). */
export async function patchOrgSkill(
  orgId: string,
  skillId: string,
  body: OrgSkillPatchRequest,
): Promise<WithSkillSync<OrgSkill>> {
  const r = await client().patch<WithSkillSync<OrgSkill>>(
    `${V1}/orgs/${enc(orgId)}/skills/${enc(skillId)}`,
    body,
  );
  return r.data;
}

/** Archive / unarchive. 409 (e.g. code `mandatory_assigned`) while the skill
 *  has a mandatory assignment. */
export async function setOrgSkillArchived(
  orgId: string,
  skillId: string,
  archived: boolean,
): Promise<WithSkillSync<OrgSkill>> {
  return patchOrgSkill(orgId, skillId, { archived });
}

// ---------------------------------------------------------------------------
// Publish requests
// ---------------------------------------------------------------------------

export async function listSkillPublishRequests(
  orgId: string,
  status?: SkillPublishRequestStatus,
): Promise<SkillPublishRequest[]> {
  const r = await client().get<unknown>(
    `${V1}/orgs/${enc(orgId)}/skill-publish-requests`,
    { params: status ? { status } : undefined },
  );
  return asList<SkillPublishRequest>(r.data, 'items');
}

/** One request WITH the locked version's `body` (approval snapshot). */
export async function getSkillPublishRequest(
  orgId: string,
  requestId: string,
): Promise<SkillPublishRequest> {
  const r = await client().get<SkillPublishRequest>(
    `${V1}/orgs/${enc(orgId)}/skill-publish-requests/${enc(requestId)}`,
  );
  return r.data;
}

/** Approve (another admin / platform admin). 409 = own request or already decided. */
export async function approveSkillPublishRequest(
  orgId: string,
  requestId: string,
): Promise<WithSkillSync<SkillPublishRequest>> {
  const r = await client().post<WithSkillSync<SkillPublishRequest>>(
    `${V1}/orgs/${enc(orgId)}/skill-publish-requests/${enc(requestId)}/approve`,
  );
  return r.data;
}

/** Reject with a REQUIRED note (422 without; 409 own / already decided). */
export async function rejectSkillPublishRequest(
  orgId: string,
  requestId: string,
  note: string,
): Promise<SkillPublishRequest> {
  const r = await client().post<SkillPublishRequest>(
    `${V1}/orgs/${enc(orgId)}/skill-publish-requests/${enc(requestId)}/reject`,
    { note },
  );
  return r.data;
}

/** Requester only, pending only; anything else → 409. */
export async function withdrawSkillPublishRequest(
  orgId: string,
  requestId: string,
): Promise<SkillPublishRequest> {
  const r = await client().post<SkillPublishRequest>(
    `${V1}/orgs/${enc(orgId)}/skill-publish-requests/${enc(requestId)}/withdraw`,
  );
  return r.data;
}

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

/**
 * Rename by department ID (`core/org_departments.py::rename_department`).
 * 409 duplicate name / folder, 403 not an admin, 502 file side failed and was
 * rolled back, 500 partially applied (manual repair).
 */
export async function renameDepartment(
  orgId: string,
  departmentId: string,
  name: string,
): Promise<RenameDepartmentResult> {
  const r = await client().patch<RenameDepartmentResult>(
    `${V1}/orgs/${enc(orgId)}/departments/${enc(departmentId)}`,
    { name },
  );
  return r.data;
}

/**
 * Departments WITH ids, from `GET /orgs/{org_id}/departments`
 * (`department_states[].id`, SKC-T5.1). `services/api.ts::listOrgDepartmentStates`
 * normalizes the states to `{name, paused}`, dropping the id, hence this reader.
 */
export async function listOrgDepartmentRecords(orgId: string): Promise<OrgDepartmentRecord[]> {
  const r = await client().get<{ departments?: unknown; department_states?: unknown }>(
    `${V1}/orgs/${enc(orgId)}/departments`,
  );
  const states = Array.isArray(r.data?.department_states) ? (r.data.department_states as unknown[]) : [];
  const out: OrgDepartmentRecord[] = [];
  states.forEach((raw) => {
    const o = (raw || {}) as Record<string, unknown>;
    if (typeof o.name !== 'string') return;
    const id = typeof o.id === 'string' ? o.id : typeof o.department_id === 'string' ? o.department_id : null;
    out.push({ id, name: o.name, paused: o.paused === true });
  });
  if (out.length === 0 && Array.isArray(r.data?.departments)) {
    (r.data.departments as unknown[]).forEach((n) => {
      if (typeof n === 'string') out.push({ id: null, name: n, paused: false });
    });
  }
  return out;
}

/** Pending publish requests of an org (OrgShell entry ④ badge); 0 on any error. */
export async function loadSkillPublishPendingCount(orgId: string): Promise<number> {
  try {
    const rows = await listSkillPublishRequests(orgId, 'pending');
    return rows.filter((r) => r.status === 'pending').length;
  } catch {
    return 0;
  }
}
